
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from "@/integrations/supabase/client";
import { setupFormSchema, SetupFormValues } from './schema';

export function useSetupAdmin() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responseDetails, setResponseDetails] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const form = useForm<SetupFormValues>({
    resolver: zodResolver(setupFormSchema),
    defaultValues: {
      email: '',
      password: '',
      setupKey: '',
    },
  });

  const onSubmit = async (values: SetupFormValues) => {
    setIsSubmitting(true);
    setError(null);
    setResponseDetails(null);
    
    try {
      console.log('Calling edge function with:', values.email);
      
      // Use the supabase client to call the edge function
      const { data, error: fnError } = await supabase.functions.invoke('setup-admin', {
        body: {
          email: values.email,
          password: values.password,
          setupKey: values.setupKey,
        },
      });

      console.log('Response data:', data);
      
      if (fnError) {
        const errorMessage = fnError.message || 'Failed to create admin user';
        console.error('Edge function error:', fnError);
        
        // Extract more details from the error if available
        let errorDetails = null;
        try {
          if (typeof fnError.context === 'object' && fnError.context) {
            errorDetails = JSON.stringify(fnError.context, null, 2);
          }
        } catch (e) {
          console.error('Error parsing error details:', e);
        }
        
        // Create an error object with the cause property
        const enhancedError = new Error(errorMessage);
        // @ts-ignore - TypeScript doesn't recognize the cause property on Error by default
        enhancedError.cause = errorDetails;
        throw enhancedError;
      }

      if (!data) {
        throw new Error('No response from server');
      }
      
      toast({
        title: 'Success',
        description: 'Admin user created successfully. You can now log in.',
        duration: 5000,
      });
      
      navigate('/login');
    } catch (error: any) {
      console.error('Error creating admin user:', error);
      
      // Set the main error message
      setError(error.message || 'Failed to create admin user');
      
      // Set additional error details if available
      if (error.cause) {
        setResponseDetails(error.cause);
      }
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to create admin user',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    error,
    responseDetails,
    navigate
  };
}
